import { Service } from '@turizi/shared';
import { useTranslation } from "react-i18next";
import { useState, useEffect, memo } from "react";
import { NumberInput, Switch, SegmentedControl, Loader, Select } from '@mantine/core';
import { IconWorld, IconLock } from '@tabler/icons-react';
import { useUpdateService } from '@/hooks/api-hooks';
import { localTenant } from '@/services/session-service';

interface Props {
    service: Service;
}

const EditServiceOperations = memo(function EditServiceOperations({ service }: Props) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<Service>(service);
    const [savingSection, setSavingSection] = useState<string>('none');
    const tenant = localTenant();

    const afipSettings: any = tenant?.settings?.afip;
    const rawSalesPoints: any[] = Array.isArray(afipSettings?.sales_points) && afipSettings.sales_points.length > 0
        ? afipSettings.sales_points
        : Array.isArray(afipSettings?.all_sales_points) && afipSettings.all_sales_points.length > 0
            ? afipSettings.all_sales_points
            : (afipSettings?.pos_number ? [{ number: afipSettings.pos_number, name: afipSettings.pos_name || 'Turizi POS', system: afipSettings.pos_system || '' }] : []);

    let posSelectData = rawSalesPoints.map((p: any) => {
        const numStr = String(p.number ?? p.numero ?? p.Nro ?? p.id ?? '').trim();
        const nameStr = String(p.name || p.displayName || p.nombreFantasia || p.Nombre || '').trim();
        const sysStr = String(p.system || p.sistema || p.EmisionTipo || p.Sistema || '').trim();
        const label = `POS ${numStr}${nameStr ? ` - ${nameStr}` : ''}${sysStr ? ` (${sysStr})` : ''}`;
        return { value: numStr, label };
    }).filter((item) => item.value !== '' && item.value !== 'undefined' && item.value !== 'null');

    if (draft.pos != null && !posSelectData.some(p => p.value === String(draft.pos))) {
        posSelectData.unshift({
            value: String(draft.pos),
            label: `POS ${draft.pos}`
        });
    }

    const {
        mutate: updateService,
        isPending: saving
    } = useUpdateService();

    const saveChanges = async (key: string, val: any) => {
        setSavingSection(key);
        updateService({
            id: service.id,
            [key]: val,
        }, {
            onSuccess: () => {
                setSavingSection('none');
            }
        });
    };

    const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>, key: string, val: any) => {
        if (e.key === 'Enter') {
            saveChanges(key, val);
        }
    };

    useEffect(() => {
        setDraft(service);
    }, [service]);

    return (
        <div className="space-y-6">
            {/* Top Featured Section: Online Publication Status */}
            <div className={`p-4 sm:p-5 rounded-2xl border transition-all duration-200 ${
                draft.publish_price
                    ? 'bg-emerald-50/70 border-emerald-300 ring-1 ring-emerald-500/20 shadow-xs'
                    : 'bg-white border-gray-200/90 shadow-2xs hover:border-gray-300'
            }`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3.5 min-w-0 flex-1">
                        <div className={`p-2.5 rounded-xl shrink-0 ${
                            draft.publish_price ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                            {draft.publish_price ? <IconWorld size={22} stroke={2} /> : <IconLock size={22} stroke={1.8} />}
                        </div>
                        <div className="space-y-1 min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-900 text-sm sm:text-base">{t('publish_price')}</span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xxs font-bold uppercase tracking-wider ${
                                    draft.publish_price
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : 'bg-gray-100 text-gray-600'
                                }`}>
                                    {draft.publish_price && <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />}
                                    {draft.publish_price ? t('published') : t('internal_use')}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                {t('publish_price_description')}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        {savingSection === 'publish_price' && <Loader size="xs" color="black" />}
                        <SegmentedControl
                            size="sm"
                            disabled={savingSection === 'publish_price'}
                            value={draft.publish_price ? 'true' : 'false'}
                            onChange={(val) => {
                                const isPublished = val === 'true';
                                setDraft({ ...draft, publish_price: isPublished });
                                saveChanges('publish_price', isPublished);
                            }}
                            data={[
                                { label: t('internal_use'), value: 'false' },
                                { label: t('published'), value: 'true' }
                            ]}
                            color={draft.publish_price ? 'green' : 'gray'}
                            classNames={{ label: 'font-semibold text-xs px-3' }}
                        />
                    </div>
                </div>
            </div>

            {/* Section: Capacity Limits */}
            <div className="solid-box-border space-y-4">
                <div className="text-sm font-semibold text-gray-800">{t('operations_and_capacity')}</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <NumberInput
                        className="w-full"
                        label={t('min_pax_per_booking')}
                        placeholder="0"
                        value={draft.min_pax_per_booking}
                        onChange={(value) => setDraft({ ...draft, min_pax_per_booking: Number(value) || undefined })}
                        onBlur={() => saveChanges('min_pax_per_booking', draft.min_pax_per_booking)}
                        onKeyDown={(e) => handleEnter(e, 'min_pax_per_booking', draft.min_pax_per_booking)}
                        min={0}
                        rightSection={savingSection === 'min_pax_per_booking' ? <Loader size="xs" color="black" /> : null}
                        hideControls
                    />
                    <NumberInput
                        className="w-full"
                        label={t('max_pax_per_booking')}
                        placeholder="--"
                        value={draft.max_pax_per_booking}
                        onChange={(value) => setDraft({ ...draft, max_pax_per_booking: Number(value) || undefined })}
                        onBlur={() => saveChanges('max_pax_per_booking', draft.max_pax_per_booking)}
                        onKeyDown={(e) => handleEnter(e, 'max_pax_per_booking', draft.max_pax_per_booking)}
                        min={0}
                        rightSection={savingSection === 'max_pax_per_booking' ? <Loader size="xs" color="black" /> : null}
                        hideControls
                    />
                    <NumberInput
                        className="w-full"
                        label={t('default_inventory_slots')}
                        description={t('default_inventory_slots_desc')}
                        placeholder="--"
                        value={draft.default_inventory_slots}
                        onChange={(value) => setDraft({ ...draft, default_inventory_slots: Number(value) || undefined })}
                        onBlur={() => saveChanges('default_inventory_slots', draft.default_inventory_slots)}
                        onKeyDown={(e) => handleEnter(e, 'default_inventory_slots', draft.default_inventory_slots)}
                        min={0}
                        rightSection={savingSection === 'default_inventory_slots' ? <Loader size="xs" color="black" /> : null}
                        hideControls
                    />
                </div>
            </div>

            {/* Section: Tax and AFIP Invoicing Settings */}
            <div className="solid-box-border space-y-4">
                <div>
                    <div className="text-sm font-semibold text-gray-800">{t('tax_invoicing_settings')}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{t('tax_percentage_desc')}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Select
                        label={t('pos_label')}
                        description={t('pos_desc')}
                        placeholder={t('select_pos_placeholder')}
                        value={draft.pos != null ? String(draft.pos) : null}
                        onChange={(value) => {
                            const newPos = value ? parseInt(value, 10) : null;
                            setDraft({ ...draft, pos: newPos });
                            saveChanges('pos', newPos);
                        }}
                        data={posSelectData}
                        searchable
                        clearable
                        rightSection={savingSection === 'pos' ? <Loader size="xs" color="black" /> : null}
                    />
                    <Select
                        label={t('tax_percentage')}
                        description={t('tax_percentage_desc')}
                        placeholder={t('select_tax_percentage')}
                        value={draft.tax_percentage != null ? String(draft.tax_percentage) : null}
                        onChange={(value) => {
                            const newTax = value !== null && value !== '' ? parseFloat(value) : null;
                            setDraft({ ...draft, tax_percentage: newTax });
                            saveChanges('tax_percentage', newTax);
                        }}
                        data={[
                            { value: '21', label: '21%' },
                            { value: '10.5', label: '10.5%' },
                            { value: '27', label: '27%' },
                            { value: '0', label: '0% (Exento)' }
                        ]}
                        clearable
                        rightSection={savingSection === 'tax_percentage' ? <Loader size="xs" color="black" /> : null}
                    />
                </div>
            </div>

            {/* Section: Publishing Settings */}
            <div className="solid-box-border space-y-5">
                <div className="text-sm font-semibold text-gray-800">{t('online_publishing')}</div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
                    {/* Rank */}
                    <div className="w-full">
                        <NumberInput
                            label={t('rank')}
                            placeholder="--"
                            value={draft.rank}
                            onChange={(value) => setDraft({ ...draft, rank: Number(value) || undefined })}
                            onBlur={() => saveChanges('rank', draft.rank)}
                            onKeyDown={(e) => handleEnter(e, 'rank', draft.rank)}
                            min={0}
                            rightSection={savingSection === 'rank' ? <Loader size="xs" color="black" /> : null}
                            hideControls
                        />
                    </div>

                    {/* Toggles */}
                    <div className="w-full lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {/* Show on main page */}
                        <div className="solid-box-border-tinted px-3.5 py-2.5 rounded-xl flex items-center justify-between gap-3 min-h-[46px] border border-gray-200/60 bg-gray-50/60 shadow-2xs">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="text-xs font-semibold text-slate-700 leading-snug">{t('show_on_main_page')}</span>
                                {savingSection === 'show_on_main_page' && <Loader size={12} color="black" className="shrink-0" />}
                            </div>
                            <Switch
                                disabled={savingSection === 'show_on_main_page'}
                                checked={service?.show_on_main_page}
                                onChange={(e) => {
                                    setDraft({ ...draft, show_on_main_page: e.target.checked });
                                    saveChanges('show_on_main_page', e.target.checked);
                                }}
                                size="xs"
                                color="blue"
                                className="shrink-0"
                            />
                        </div>

                        {/* Favorite */}
                        <div className="solid-box-border-tinted px-3.5 py-2.5 rounded-xl flex items-center justify-between gap-3 min-h-[46px] border border-gray-200/60 bg-gray-50/60 shadow-2xs">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="text-xs font-semibold text-slate-700 leading-snug">{t('favorite')}</span>
                                {savingSection === 'is_favorite' && <Loader size={12} color="black" className="shrink-0" />}
                            </div>
                            <Switch
                                disabled={savingSection === 'is_favorite'}
                                checked={service?.is_favorite}
                                onChange={(e) => {
                                    setDraft({ ...draft, is_favorite: e.target.checked });
                                    saveChanges('is_favorite', e.target.checked);
                                }}
                                size="xs"
                                color="blue"
                                className="shrink-0"
                            />
                        </div>

                        {/* Available 3rd party */}
                        <div className="solid-box-border-tinted px-3.5 py-2.5 rounded-xl flex items-center justify-between gap-3 min-h-[46px] border border-gray-200/60 bg-gray-50/60 shadow-2xs">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="text-xs font-semibold text-slate-700 leading-snug">{t('available_3rd_party')}</span>
                                {savingSection === 'available_3rd_party' && <Loader size={12} color="black" className="shrink-0" />}
                            </div>
                            <Switch
                                disabled={savingSection === 'available_3rd_party'}
                                checked={service?.available_3rd_party}
                                onChange={(e) => {
                                    setDraft({ ...draft, available_3rd_party: e.target.checked });
                                    saveChanges('available_3rd_party', e.target.checked);
                                }}
                                size="xs"
                                color="blue"
                                className="shrink-0"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default EditServiceOperations;
